import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, Heart, Users, Calendar } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LOGO = "https://customer-assets.emergentagent.com/job_pitch-connect-40/artifacts/yd54d9mb_D6EC86DD-43FE-4578-97EA-A54FBCABC788.png";

// Confirmation "You're in!" page shown after a user registers via an ad campaign
// (e.g. FB / Instagram / Google ads pointing at /register?src=ad). Also serves as
// the campaign's success/conversion URL. Works as a normal in-app screen for
// users navigating there directly.
export default function Welcome() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  const firstName = useMemo(() => {
    const n = user?.name || "";
    return n.split(" ")[0] || "";
  }, [user]);

  const goNext = () => {
    if (!user) nav("/register");
    else if (user.profile_complete) nav("/discover");
    else nav("/onboarding");
  };

  return (
    <div className="min-h-screen bg-footy-bg text-white grain overflow-hidden relative" data-testid="welcome-page">
      {/* Ambient red glow behind the tick */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[60vh] pointer-events-none opacity-70"
        style={{
          background:
            "radial-gradient(circle at 50% 20%, rgba(230,57,70,0.35), rgba(230,57,70,0.05) 40%, transparent 65%)",
        }}
      />

      <div className="relative max-w-md mx-auto px-6 pt-[calc(2.5rem+env(safe-area-inset-top))] pb-[calc(2rem+env(safe-area-inset-bottom))] flex flex-col min-h-screen">
        {/* Header logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-10" data-testid="welcome-logo">
          <img src={LOGO} alt="" className="w-11 h-11 rounded-full" />
          <span className="font-display text-2xl tracking-wider text-white">FOOTYMATCH</span>
        </Link>

        {/* Big tick */}
        <div
          className={`mx-auto w-24 h-24 rounded-full bg-footy-red/15 border-2 border-footy-red flex items-center justify-center transition-all duration-500 ${
            mounted ? "scale-100 opacity-100" : "scale-75 opacity-0"
          }`}
          data-testid="welcome-tick"
        >
          <CheckCircle2 size={54} className="text-footy-red" strokeWidth={2.2} />
        </div>

        {/* Copy */}
        <div className="text-center mt-8">
          <p className="text-[11px] uppercase tracking-[0.3em] text-footy-red font-bold" data-testid="welcome-eyebrow">
            You&apos;re in
          </p>
          <h1 className="font-display text-4xl sm:text-5xl uppercase leading-[1.05] tracking-tight mt-3" data-testid="welcome-heading">
            Welcome to the terrace{firstName ? `, ${firstName}` : ""}
          </h1>
          <p className="text-slate-300 mt-4 text-base leading-relaxed" data-testid="welcome-subtitle">
            You&apos;ve just joined a community built by football fans, for football fans.
            No swiping into the void — every match starts with the game.
          </p>
        </div>

        {/* What's next — 3 quick pillars */}
        <div className="mt-9 space-y-3" data-testid="welcome-pillars">
          <Pillar
            icon={<Heart size={16} />}
            title="Find a partner who gets your Saturdays"
            body="Match on club, banter tolerance and the way you follow the game."
          />
          <Pillar
            icon={<Users size={16} />}
            title="Or just find fellow fans"
            body="Match-day mates, five-a-side crews, and away-day travel buddies."
          />
          <Pillar
            icon={<Calendar size={16} />}
            title="Never watch a game alone"
            body="Post the fixture you&apos;re heading to and see who else is going."
          />
        </div>

        {/* CTA */}
        <div className="mt-auto pt-10">
          <button
            onClick={goNext}
            className="footy-btn-primary w-full text-base py-3.5 inline-flex items-center justify-center gap-2"
            data-testid="welcome-continue-btn"
          >
            {user ? (user.profile_complete ? "Continue to Footymatch" : "Build your profile") : "Create your account"} <ArrowRight size={16} />
          </button>
          <p className="text-center text-[11px] text-slate-500 mt-3">
            Takes about a minute · No credit card · 18+
          </p>
        </div>
      </div>
    </div>
  );
}

function Pillar({ icon, title, body }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-2xl bg-white/[0.04] border border-white/10">
      <div className="w-9 h-9 rounded-lg bg-footy-red/15 text-footy-red flex items-center justify-center flex-none">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="font-bold text-sm leading-tight">{title}</div>
        <p className="text-xs text-slate-400 mt-1 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
